import { z } from "zod"

import { brandingSchema, hasBrandingChange, updateBrandingFields } from "./branding.ts"
import {
  createCustomerRequestSchema,
  customerSchema,
  updateCustomerRequestSchema,
} from "./customers.ts"
import {
  invoiceBodyShape,
  invoiceLineRequestSchema,
  invoiceLineSchema,
  invoiceSchema,
  INVOICE_MAX_LINES,
  sellerProfileFieldValidators,
  sellerProfileSchema,
} from "./invoices.ts"
import { receiptSchema } from "./payment-receipts.ts"
import { paymentSchema, recordPaymentRequestSchema } from "./payments.ts"
import { revisionSchema } from "./sync.ts"
import { tripHoldReasonSchema, tripVisibilitySchema } from "./trips.ts"

/**
 * Invoicing as synchronized entities (T511, docs/specs/invoice-sync-plan.md). Content syncs;
 * numbers, the sent/void lifecycle, sending, PDFs and quota are server actions. Stored/response
 * schemas are lenient (no `.max()`, no narrowed enums: the server parses every stored row with
 * them); push requests are strict. Field names and nullability are identical in Postgres, GRDB
 * and Room (`base_revision` and `dirty` exist on the client tables only).
 */

/** The entities of the `invoicing` feature, in dependency order (a parent before its children). */
export const INVOICE_SYNC_ENTITIES = [
  "customers",
  "invoices",
  "payments",
  "payment_receipts",
  "organisation_branding",
  "seller_profiles",
] as const

const syncMeta = {
  visibility: tripVisibilitySchema.default("full"),
  hold_reason: tripHoldReasonSchema.nullable().default(null),
  revision: revisionSchema,
}

const deletedAt = z.string().datetime({ offset: true }).nullable().default(null)

export const customerSyncSchema = customerSchema.extend(syncMeta)
export type CustomerSync = z.infer<typeof customerSyncSchema>

/**
 * An invoice with its lines (a child collection of the document, replaced as a whole). The
 * brand snapshot is trimmed to template and colour: the inline logo stays server side (the
 * PDF is the sent artifact).
 */
export const invoiceSyncSchema = invoiceSchema.omit({ brand_snapshot: true }).extend({
  ...syncMeta,
  brand_snapshot: z
    .object({
      template: z.string().default("classic"),
      brand_color: z.string().nullable().default(null),
    })
    .nullable()
    .default(null),
  lines: z.array(invoiceLineSchema).default([]),
})
export type InvoiceSync = z.infer<typeof invoiceSyncSchema>

export const paymentSyncSchema = paymentSchema.extend(syncMeta)
export type PaymentSync = z.infer<typeof paymentSyncSchema>

export const paymentReceiptSyncSchema = receiptSchema.extend({ ...syncMeta, deleted_at: deletedAt })
export type PaymentReceiptSync = z.infer<typeof paymentReceiptSyncSchema>

/** Keyed by the organisation id (1:1). `default_template` is required: it tells the union apart. */
export const organisationBrandingSyncSchema = brandingSchema.extend({
  ...syncMeta,
  default_template: z.string(),
  deleted_at: deletedAt,
})
export type OrganisationBrandingSync = z.infer<typeof organisationBrandingSyncSchema>

/** Keyed by the organisation id (1:1); never deleted. `invoice_prefix` is required (union tag). */
export const sellerProfileSyncSchema = sellerProfileSchema.extend({
  organisation_id: z.uuid(),
  ...syncMeta,
  invoice_prefix: z.string(),
  deleted_at: deletedAt,
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
})
export type SellerProfileSync = z.infer<typeof sellerProfileSyncSchema>

// --- Push requests (strict) -------------------------------------------------------------------

const baseRevision = z.number().int().nonnegative().optional()

export const createCustomerSyncRequestSchema = createCustomerRequestSchema
export const patchCustomerSyncRequestSchema = updateCustomerRequestSchema.extend({
  base_revision: baseRevision,
})

/** Create is the web create request: a draft with at least one line. */
export { createInvoiceRequestSchema as createInvoiceSyncRequestSchema } from "./invoices.ts"

/**
 * Draft patch: any subset of the editable fields; `lines`, when present, replaces the whole
 * list. Server-owned fields (number, status, totals, send state, snapshot) are not accepted.
 * `due_date` against `issue_date` is checked here when both are sent and by the adapter
 * against the stored row otherwise.
 */
export const patchInvoiceSyncRequestSchema = z
  .strictObject({
    base_revision: baseRevision,
    customer_id: invoiceBodyShape.customer_id.unwrap().optional(),
    buyer_name: invoiceBodyShape.buyer_name.unwrap().optional(),
    buyer_abn: invoiceBodyShape.buyer_abn.unwrap().optional(),
    issue_date: invoiceBodyShape.issue_date.optional(),
    due_date: invoiceBodyShape.due_date.optional(),
    amounts_are: invoiceBodyShape.amounts_are.optional(),
    gst_rounding: invoiceBodyShape.gst_rounding.optional(),
    note: invoiceBodyShape.note.unwrap().optional(),
    show_bank_details: invoiceBodyShape.show_bank_details.unwrap().optional(),
    template: invoiceBodyShape.template.unwrap().optional(),
    lines: z.array(invoiceLineRequestSchema).min(1).max(INVOICE_MAX_LINES).optional(),
  })
  .refine(
    (patch) =>
      patch.issue_date === undefined ||
      patch.due_date === undefined ||
      patch.due_date >= patch.issue_date,
    { message: "due_date must not be before issue_date", path: ["due_date"] },
  )
export type PatchInvoiceSyncRequest = z.infer<typeof patchInvoiceSyncRequestSchema>

export const createPaymentSyncRequestSchema = recordPaymentRequestSchema.extend({
  invoice_id: z.uuid(),
})
export type CreatePaymentSyncRequest = z.input<typeof createPaymentSyncRequestSchema>

export const patchOrganisationBrandingSyncRequestSchema = z
  .strictObject({ ...updateBrandingFields, base_revision: baseRevision })
  .refine(hasBrandingChange, { message: "Nothing to update" })

/** Seller profile patch: the form validators without their defaults, so absent stays absent. */
export const patchSellerProfileSyncRequestSchema = z.strictObject({
  base_revision: baseRevision,
  ...Object.fromEntries(
    Object.entries(sellerProfileFieldValidators).map(([key, validator]) => [
      key,
      validator.optional(),
    ]),
  ),
} as { base_revision: typeof baseRevision } & {
  [K in keyof typeof sellerProfileFieldValidators]: z.ZodOptional<
    (typeof sellerProfileFieldValidators)[K]
  >
})
export type PatchSellerProfileSyncRequest = z.infer<typeof patchSellerProfileSyncRequestSchema>
